import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaHome, FaTimes, FaClipboardList, FaDice, FaApple,
  FaGamepad, FaTrophy,
  FaPlane, FaFighterJet, FaGift, FaListUl, FaInfoCircle,
} from 'react-icons/fa';
import { MdLiveTv, MdSportsEsports } from 'react-icons/md';
import { IoLogoAndroid } from 'react-icons/io';
import { GiCardAceSpades } from 'react-icons/gi';
import { Icons } from '@/utils/Icons';
import { useLazyGetMatchesQuery, useLazyGetSportCompetitionsQuery } from '@/store/api/sportsApi';
import { useGetApkConfigQuery } from '@/store/api/systemApi';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { openAuthModal, setRulesModalOpen } from '@/store/slices/uiSlice';
import { useAggregatorLauncher } from '@/hooks/useAggregatorLauncher';
import { formatNumber } from '@/utils';
import { isNativeApp } from '@/utils/platform';
import type { BrandFeature } from '@/brands';
import { brand, isMenuLinkPainted, useBrandFeatures, useBrandMenus, useSiteSettings } from '@/brands';

/**
 * The two crash games the drawer opens by name rather than by lobby.
 *
 * `gameCode` is the aggregator's own id, verified against GET /aggregator/games
 * on this backend: JetX is Smartsoft's, aviatrix is the Aviatrix studio's only
 * title. They are named rows because that is how players ask for them — sending
 * them to a Crash lobby to hunt for the tile is the thing this row exists to
 * avoid. If a brand's aggregator account drops either game the launch call
 * answers with an error toast, which is the same thing a dead tile would do.
 */
const DRAWER_GAMES = {
  jetx: '2750',
  aviatrix: '10518',
} as const;

/* ───────────────── Types ───────────────── */

type TreeNode = {
  id: string;
  label: string;
  children?: TreeNode[];
  sportName?: string;
  seriesId?: string;
  path?: string;
  /**
   * Brand Feature this row belongs to. Menu Visibility (`id`) is the brand
   * owner hiding a link; this is the super admin revoking the product behind
   * it, which also 404s the path — so the row has to go, not just grey out.
   */
  grant?: BrandFeature;
  /**
   * The backend's `SportType` member to query competitions for, when it differs
   * from the label. `/competitions?sport=` feeds this straight into Prisma, so
   * it has to be an exact enum member — "Horse Racing" is a 500, `HorseRacing`
   * is the row. Absent means the label doubles as the key (Cricket, Tennis);
   * `null` means this backend carries no such sport, so it is never queried.
   */
  sport?: string | null;
};

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  /**
   * Drops the desktop sports rail so the page runs full-width. The mobile
   * drawer always stays — the header's hamburger is the only navigation on
   * small screens, and hiding it would leave a dead button.
   */
  hideDesktopRail?: boolean;
}

/* ───────────────── Desktop Sports Tree ───────────────── */

const racingTree: TreeNode[] = [
  { id: 'horse', label: 'Horse Racing', sport: 'HorseRacing' },
  // `GreyhoundRacing` is commented out of the server's SportType enum, so there
  // is no value to query with — see the note on `sport` above.
  { id: 'greyhound', label: 'Greyhound Racing', sport: null },
];

const othersTree: TreeNode[] = [
  { id: 'casino', label: 'Our Casino', path: '/casino/all', grant: 'casino' },
  { id: 'vip', label: 'Our VIP Casino', path: '/casino/all/live', grant: 'casino' },
  { id: 'premium', label: 'Our Premium Casino', path: '/casino/all/table', grant: 'casino' },
  { id: 'virtual', label: 'Our Virtual', path: '/casino/all/virtual', grant: 'casino' },
  { id: 'live', label: 'Live Casino', path: '/casino/all/live', grant: 'casino' },
  { id: 'slot', label: 'Slot Game', path: '/casino/all/slots', grant: 'casino' },
  // Labelled Fantasy Game, so it answers to the Fantasy switch even though the
  // lobby it opens is served by the casino aggregator.
  { id: 'fantasy', label: 'Fantasy Game', path: '/casino/all/fishing', grant: 'fantasy' },
];

const sportsTree: TreeNode[] = [
  { id: 'politics', label: 'Politics' }, { id: 'cricket', label: 'Cricket' },
  { id: 'football', label: 'Football' }, { id: 'tennis', label: 'Tennis' },
  { id: 'table-tennis', label: 'Table Tennis' }, { id: 'badminton', label: 'Badminton' },
  { id: 'esoccer', label: 'Esoccer' }, { id: 'basketball', label: 'Basketball' },
  { id: 'volleyball', label: 'Volleyball' }, { id: 'snooker', label: 'Snooker' },
  { id: 'ice-hockey', label: 'Ice Hockey' }, { id: 'e-games', label: 'E Games' },
  { id: 'futsal', label: 'Futsal' }, { id: 'handball', label: 'Handball' },
  { id: 'kabaddi', label: 'Kabaddi' }, { id: 'golf', label: 'Golf' },
  { id: 'rugby-league', label: 'Rugby League' }, { id: 'boxing', label: 'Boxing' },
  { id: 'mma', label: 'Mixed Martial Arts' }, { id: 'motogp', label: 'MotoGP' },
  { id: 'american-football', label: 'American Football' }, { id: 'soccer', label: 'Soccer' },
  { id: 'wrestling', label: 'Wrestling' },
];

function DesktopSportsTree() {
  const navigate = useNavigate();
  // Per-brand Menu Visibility. A node's `id` doubles as its super-admin key, so
  // the rail needs no lookup table — an id the panel does not know stays visible.
  const menus = useBrandMenus();
  const { sidebar } = menus;
  // Per-brand Features on top of that: Menu Visibility hides a link the product
  // still has, Features take the product away entirely. Racing and All Sports
  // are the sportsbook, so they stand or fall with the `sports` grant.
  const features = useBrandFeatures();
  const granted = (node: TreeNode) => !node.grant || features[node.grant] !== false;
  const sportsOn = features.sports !== false;
  const shown = (id: string) => sidebar[id as keyof typeof sidebar] !== false;
  // Racing and All Sports rows expand the tree in place, so they own no URL and
  // their own toggle is the whole test. The Others rows DO navigate, and several
  // of them share a page with each other and with the top nav — "Our VIP Casino"
  // and "Live Casino" are both `/casino/all/live`. Hiding any one of those closes
  // the page, so `isMenuLinkPainted` drops the rest rather than leaving the rail
  // with a row that 404s.
  const racing = sportsOn ? racingTree.filter((n) => shown(n.id)) : [];
  const others = othersTree.filter(
    (n) => granted(n) && isMenuLinkPainted(n.path, shown(n.id), menus),
  );
  const allSports = sportsOn ? sportsTree.filter((n) => shown(n.id)) : [];
  const [getCompetitions] = useLazyGetSportCompetitionsQuery();
  const [getMatches] = useLazyGetMatchesQuery();
  const [openSections, setOpenSections] = useState({ racing: true, others: true, allSports: true });
  const [activeSportId, setActiveSportId] = useState<string | null>(null);
  const [activeCompetitionId, setActiveCompetitionId] = useState<string | null>(null);
  const [sportCompetitions, setSportCompetitions] = useState<Record<string, TreeNode[]>>({});
  const [competitionMatches, setCompetitionMatches] = useState<Record<string, TreeNode[]>>({});

  const toggleSection = (key: keyof typeof openSections) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleNode = async (node: TreeNode, level: number) => {
    if (level === 0) {
      const isAlreadyOpen = activeSportId === node.id;
      if (isAlreadyOpen) { setActiveSportId(null); setActiveCompetitionId(null); return; }
      setActiveSportId(node.id);
      setActiveCompetitionId(null);
      if (!sportCompetitions[node.id]) {
        // `sport: null` means this backend has no such sport at all. Querying
        // anyway sends a value Prisma cannot parse and answers 500, so the row
        // opens on an empty list instead.
        const sportKey = node.sport === undefined ? node.label : node.sport;
        const res = sportKey ? await getCompetitions(sportKey).unwrap().catch(() => null) : null;
        setSportCompetitions((prev) => ({
          ...prev,
          [node.id]: res?.competitions?.map((comp) => ({
            id: `comp-${comp.id}`, label: comp.name, sportName: sportKey ?? node.label, seriesId: String(comp.id),
          })) || [],
        }));
      }
      return;
    }
    if (level === 1 && node.seriesId) {
      const isAlreadyOpen = activeCompetitionId === node.seriesId;
      if (isAlreadyOpen) { setActiveCompetitionId(null); return; }
      setActiveCompetitionId(node.seriesId);
      if (!competitionMatches[node.seriesId]) {
        const res = await getMatches({ sportName: node.sportName?.toLowerCase(), seriesId: node.seriesId }).unwrap();
        setCompetitionMatches((prev) => ({
          ...prev,
          [node.seriesId!]: res?.fixture?.map((m) => ({ id: String(m.id), label: m.name, sportName: m.sport })) || [],
        }));
      }
    }
  };

  const TreeItem = ({ node, level = 0, section }: { node: TreeNode; level?: number; section: 'racing' | 'others' | 'allSports' }) => {
    const isOpen = level === 0 ? activeSportId === node.id : level === 1 ? activeCompetitionId === node.seriesId : false;
    const dynamicChildren = level === 0 ? sportCompetitions[node.id] : level === 1 && node.seriesId ? competitionMatches[node.seriesId] : undefined;
    const children = dynamicChildren || node.children;
    // Racing rows expand a competition tree in place exactly like All Sports
    // ones. This used to read `section === 'allSports'`, and since it gates the
    // click as well as the icon, every Racing row fell through `onClick` without
    // matching a single branch — a button that did nothing at all.
    const showToggleIcon =
      (section === 'allSports' || section === 'racing') && (level === 0 || level === 1);
    const paddingLeft = level === 0 ? 15 : level === 1 ? 25 : 35;
    return (
      <div>
        <button
          onClick={() => {
            if (section === 'others') { navigate(node?.path ?? '#'); return; }
            if (level === 2 && node.sportName) { navigate(`/game-details/${node.sportName}/${node.id}`); return; }
            if (showToggleIcon) toggleNode(node, level);
          }}
          className="text-start w-full min-h-[28px] bg-[#d3d3d3] text-[14px] flex items-center gap-2 border-b border-gray-400 hover:bg-gray-300"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          {showToggleIcon && (isOpen ? <Icons.MinusSquare size={12} /> : <Icons.PlusSquare size={12} />)}
          {node.label}
        </button>
        {isOpen && children?.map((child) => <TreeItem key={child.id} node={child} level={level + 1} section={section} />)}
        {/* Without this an open row with no competitions renders nothing, which
            is indistinguishable from the click having been ignored. */}
        {isOpen && children && children.length === 0 && (
          <div
            className="min-h-[28px] bg-[#d3d3d3] text-[13px] text-gray-600 italic flex items-center border-b border-gray-400"
            style={{ paddingLeft: `${level === 0 ? 25 : 35}px` }}
          >
            No events
          </div>
        )}
      </div>
    );
  };

  const SectionHeader = ({ title, sectionKey }: { title: string; sectionKey: keyof typeof openSections }) => (
    <div
      onClick={() => toggleSection(sectionKey)}
      className="h-[32px] bg-primary-gradient text-white text-[15px] font-semibold flex items-center justify-between px-3 cursor-pointer"
    >
      {title}
      {openSections[sectionKey] ? <Icons.Up size={12} /> : <Icons.Down size={12} />}
    </div>
  );

  return (
    <div className="text-sm">
      {/* Player's own referral link. The desktop rail is sports-only, so without
          this the affiliate page is unreachable on desktop — it lived solely in
          the mobile drawer. */}
      {shown('referEarn') && (
        <button
          onClick={() => navigate('/user/affiliate')}
          className="h-[32px] w-full bg-primary-gradient text-white text-[15px] font-semibold flex items-center gap-2 px-3 cursor-pointer"
        >
          Refer &amp; Earn
        </button>
      )}

      {/* A section is drawn only if the super admin left it on AND something is
          left inside it — an empty header would be a dead row a player can only
          collapse. */}
      {shown('secRacing') && racing.length > 0 && (
        <>
          <SectionHeader title="Racing Sports" sectionKey="racing" />
          {openSections.racing && racing.map((node) => <TreeItem key={node.id} node={node} section="racing" />)}
        </>
      )}
      {shown('secOthers') && others.length > 0 && (
        <>
          <SectionHeader title="Others" sectionKey="others" />
          {openSections.others && others.map((node) => <TreeItem key={node.id} node={node} section="others" />)}
        </>
      )}
      {shown('secAllSports') && allSports.length > 0 && (
        <>
          <SectionHeader title="All Sports" sectionKey="allSports" />
          {openSections.allSports && allSports.map((node) => <TreeItem key={node.id} node={node} section="allSports" />)}
        </>
      )}
    </div>
  );
}

/* ───────────────── Main Sidebar ───────────────── */

/**
 * One row of the drawer's product list: a square icon plate, the label, and a
 * chevron. Flat and full-bleed, in the reference site's shape.
 */
function DrawerRow({
  icon, label, badge, isActive, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: boolean;
  isActive?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`drw-row${isActive ? ' is-active' : ''}`} onClick={onClick}>
      <span className="drw-row-ico">{icon}</span>
      {badge ? <span className="drw-row-badge">{label}</span> : <span className="drw-row-label">{label}</span>}
    </button>
  );
}

const Sidebar = ({ isOpen, onToggle, hideDesktopRail = false }: SidebarProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { info, walletInfo } = useAppSelector((state) => state.user);
  const isAuthed = useAppSelector((state) => state.auth.isAuthenticated);

  // The drawer is the only navigation on mobile, so a product the super admin
  // revoked has to disappear here too — its rows point at URLs `FeatureGate`
  // now answers 404 for.
  const { sports: sportsOn, casino: casinoOn, deposit: depositGranted, registration } =
    useBrandFeatures();
  const { deposit: cashierOn, signup: signupOn, showLogin: loginOn } = useSiteSettings();
  const canDeposit = cashierOn && depositGranted;
  const canRegister = signupOn && registration;

  // Same promotion the registration sheet offers, so the drawer's card and the
  // signup form cannot advertise two different things. Nothing configured means
  // no card rather than an invented offer.
  const promo = brand.signupBonuses?.find((b) => b.id) ?? null;

  const mainWallet = walletInfo?.find((w) => w?.type === 'Main');
  const mainBalance =
    Number(mainWallet?.amount ?? 0) -
    Math.abs(Number(mainWallet?.exposureAmount ?? 0)) -
    Number(mainWallet?.lockedAmount ?? 0);

  const username = info?.username ?? 'Guest';

  const go = (to: string) => { navigate(to); onToggle(); };
  const openAuth = (which: 'login' | 'register') => {
    dispatch(openAuthModal(which));
    // The sheet covers the drawer on a phone; leaving it open behind would put
    // two dismissable layers on screen with one backdrop between them.
    onToggle();
  };

  // Launching needs a session — /aggregator/launch is authenticated — so a
  // logged-out tap opens the sign-in sheet instead of firing a request that can
  // only come back 401 and a toast.
  const { launch } = useAggregatorLauncher();
  const playGame = (gameCode: string) => {
    if (!isAuthed) { openAuth('login'); return; }
    onToggle();
    launch(gameCode);
  };

  // Rules is a sheet, not a page: the drawer closes and the modal the header
  // already mounts takes over.
  const openRules = () => { dispatch(setRulesModalOpen(true)); onToggle(); };

  // Bonuses is behind ProtectedRoute; asking for sign-in here saves the
  // logged-out player a bounce through /login back to /home.
  const openBonuses = () => (isAuthed ? go('/bonuses') : openAuth('login'));

  return (
    <>
      <style>{DRAWER_CSS}</style>

      {/* ── Mobile Backdrop ── */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] xl:hidden" onClick={onToggle} />
      )}

      {/* ── Mobile Drawer ── */}
      <div
        className="drw xl:hidden"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(-100%)' }}
        aria-hidden={!isOpen}
      >
        {/* Halka band jis par sirf naam aur ✕. Yeh .drw-scroll ke BAHAR hai
            taaki poori chaudai le sake (scroll wale hisse me 14px padding hai)
            aur scroll karte waqt upar tika rahe.
            ✕ pehle overlay ke kaale hisse par tairta tha, drawer se alag —
            reference me wo isi band ke daayein kone me baitha hai, isliye ab
            wahi. Isse tap target bhi drawer ke andar aa gaya. */}
        <div className="drw-head">
          <span className="drw-title">Menu</span>
          <button className="drw-close" onClick={onToggle} aria-label="Close menu">
            <FaTimes size={14} />
          </button>
        </div>

        <div className="drw-scroll">

          {/* Cashier for a signed-in player, the sign-in pair for a visitor —
              a Deposit button is a dead end without an account behind it. */}
          {isAuthed ? (
            <>
              {canDeposit && (
                <button className="drw-cta" onClick={() => go('/user/deposit')}>Deposit</button>
              )}
              <div className="drw-acct">
                <span className="drw-acct-name">{username}</span>
                <span className="drw-acct-bal">{formatNumber(mainBalance)}</span>
              </div>
            </>
          ) : (
            <div className="drw-auth">
              {loginOn && (
                <button className="drw-cta drw-cta--dark" onClick={() => openAuth('login')}>Log in</button>
              )}
              {canRegister && (
                <button className="drw-cta" onClick={() => openAuth('register')}>Registration</button>
              )}
            </div>
          )}

          <DrawerApps />

          {/* Welcome offer. Takes a visitor to the signup sheet where the offer is
              actually chosen, and a signed-in player to the cashier where it is
              claimed. */}
          {promo && (
            <div className="drw-promo">
              {/* Art on top, copy under it — the reference's shape. The brand can
                  supply real artwork through `signupBonuses[].image`; the drawn
                  trophy is what shows until it does, so the panel is never a
                  blank green box waiting on an upload. */}
              {promo.image ? (
                <img className="drw-promo-art" src={promo.image} alt="" />
              ) : (
                <PromoTrophy />
              )}
              <p className="drw-promo-kicker">{promo.subtitle}</p>
              <p className="drw-promo-title">{promo.title}</p>
              <button
                className="drw-promo-btn"
                onClick={() => (isAuthed ? canDeposit && go('/user/deposit') : openAuth('register'))}
              >
                Take bonus
              </button>
            </div>
          )}

          <nav className="drw-list">
            <DrawerRow icon={<FaHome size={15} />} label="Main" isActive={pathname === '/home'} onClick={() => go('/home')} />
            {sportsOn && (
              <>
                <DrawerRow icon={<MdLiveTv size={17} />} label="Live" isActive={pathname === '/inplay'} onClick={() => go('/inplay')} />
                <DrawerRow icon={<FaTrophy size={15} />} label="Sports" isActive={pathname.startsWith('/all-sports')} onClick={() => go('/all-sports/Cricket')} />
              </>
            )}
            {casinoOn && (
              <>
                <DrawerRow icon={<FaDice size={15} />} label="Casino" isActive={pathname === '/casino/all'} onClick={() => go('/casino/all')} />
                <DrawerRow icon={<GiCardAceSpades size={16} />} label="Live Dealers" isActive={pathname.includes('/casino/all/live')} onClick={() => go('/casino/all/live')} />
                <DrawerRow icon={<FaGamepad size={15} />} label="TV Games" isActive={pathname.includes('/casino/all/virtual')} onClick={() => go('/casino/all/virtual')} />
                {/* Andar Bahar lives in the table-games lobby; the badge is the
                    reference's way of marking the local favourite. */}
                <DrawerRow icon={<FaDice size={15} />} label="Andar Bahar" badge isActive={pathname.includes('/casino/all/table')} onClick={() => go('/casino/all/table')} />
                {/* Named crash games, opened directly rather than through a
                    lobby — see DRAWER_GAMES. Only brands on the aggregator
                    carry them, so the build flag gates the pair. */}
                {brand.features.aggregator && (
                  <>
                    <DrawerRow icon={<FaPlane size={15} />} label="JetX" onClick={() => playGame(DRAWER_GAMES.jetx)} />
                    <DrawerRow icon={<FaFighterJet size={15} />} label="Aviatrix" onClick={() => playGame(DRAWER_GAMES.aviatrix)} />
                    {/* eSport is a studio, not a single title, so it opens the
                        provider's lobby the way the other category rows do. */}
                    <DrawerRow icon={<MdSportsEsports size={17} />} label="eSport" isActive={pathname.includes('/casino/dpesports')} onClick={() => go('/casino/dpesports/all')} />
                  </>
                )}
              </>
            )}
            <DrawerRow icon={<FaGift size={15} />} label="Bonuses" isActive={pathname === '/bonuses'} onClick={openBonuses} />
            <DrawerRow icon={<FaListUl size={15} />} label="Rules" onClick={openRules} />
            <DrawerRow icon={<FaInfoCircle size={15} />} label="Tutorials" isActive={pathname === '/tutorials'} onClick={() => go('/tutorials')} />
          </nav>

          <p className="drw-foot">{brand.name} · 18+ · Play responsibly</p>
        </div>
      </div>

      {/* ── Desktop Sidebar (sports tree) ── */}
      {!hideDesktopRail && (
        <aside className="hidden xl:block w-[240px] 2xl:w-[280px] bg-white border-r overflow-y-auto shrink-0">
          <DesktopSportsTree />
        </aside>
      )}
    </>
  );
};

/**
 * "Our applications" — the same pair the header carries, drawn only when each
 * icon actually leads somewhere.
 */
function DrawerApps() {
  const inApp = isNativeApp();
  const { data: apk } = useGetApkConfigQuery(undefined, { skip: inApp });
  const { canInstall, handleInstall } = usePWAInstall();
  const [tip, setTip] = useState(false);

  if (inApp) return null;
  const showApk = !!apk?.isEnabled && !!apk?.apkUrl;

  return (
    <>
      <div className="drw-apps">
        <span className="drw-apps-label">Our applications:</span>
        <span className="drw-apps-icons">
          {/* Apple is always here, as in the header: there is no iOS build, so
              it installs the PWA when the browser offers a prompt and otherwise
              shows the Add-to-Home-Screen line below — which is all an iPhone
              can do. Android still needs an actual APK to point at. */}
          <button
            onClick={() => (canInstall ? handleInstall() : setTip((v) => !v))}
            aria-label="Install app"
          >
            <FaApple size={19} />
          </button>
          {showApk && (
            <a href={apk!.apkUrl} download target="_blank" rel="noopener noreferrer" aria-label="Download Android app">
              <IoLogoAndroid size={21} />
            </a>
          )}
        </span>
      </div>
      {tip && (
        <p className="drw-apps-tip">
          On iPhone: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
        </p>
      )}
    </>
  );
}

/**
 * The promo panel's artwork when the brand has uploaded none.
 *
 * Drawn rather than shipped as a PNG: it has to sit on a green panel at any
 * drawer width, and `currentColor` is not enough — the cup, the confetti and
 * the plinth each carry their own colour. Purely decorative, so it is hidden
 * from screen readers; the offer itself is the text under it.
 */
function PromoTrophy() {
  return (
    <svg className="drw-promo-art" viewBox="0 0 120 84" aria-hidden="true">
      {/* confetti */}
      <circle cx="14" cy="16" r="3" fill="#ff5a5f" />
      <circle cx="104" cy="24" r="2.5" fill="#4fc3f7" />
      <rect x="98" y="8" width="6" height="6" rx="1.5" fill="#ffd54f" transform="rotate(20 101 11)" />
      <rect x="18" y="34" width="5" height="5" rx="1.2" fill="#ffffff" opacity=".75" transform="rotate(-25 20 36)" />
      <circle cx="30" cy="10" r="2" fill="#ffffff" opacity=".7" />
      <circle cx="90" cy="52" r="2.4" fill="#ffd54f" />
      {/* handles */}
      <path d="M40 22h-8a10 10 0 0 0 10 14" fill="none" stroke="#ffb300" strokeWidth="4" strokeLinecap="round" />
      <path d="M80 22h8a10 10 0 0 1-10 14" fill="none" stroke="#ffb300" strokeWidth="4" strokeLinecap="round" />
      {/* cup */}
      <path d="M40 16h40v14a20 20 0 0 1-40 0z" fill="#ffc107" />
      <path d="M60 16h20v14a20 20 0 0 1-20 20z" fill="#ffa000" />
      {/* stem + plinth */}
      <rect x="55" y="50" width="10" height="10" fill="#ff8f00" />
      <rect x="44" y="60" width="32" height="7" rx="2" fill="#ffb300" />
      <rect x="38" y="67" width="44" height="9" rx="3" fill="#ff8f00" />
    </svg>
  );
}

const DRAWER_CSS = `
  .drw {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 101;
    display: flex;
    flex-direction: column;
    width: 285px;
    max-width: 84vw;
    background: #fff;
    box-shadow: 0 0 40px rgba(17,17,16,.28);
    transition: transform .28s cubic-bezier(.2,.8,.3,1);
  }
  .drw-scroll {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 14px 14px calc(24px + env(safe-area-inset-bottom, 0px));
  }

  /* Sits in the header band now, not on the overlay — see the note at its JSX. */
  .drw-close {
    flex-shrink: 0;
    width: 28px; height: 28px;
    display: flex; align-items: center; justify-content: center;
    border: none; border-radius: 50%;
    background: var(--cb-ink-strong, #111110);
    color: #fff;
    cursor: pointer;
    transition: filter .15s, transform .1s;
  }
  .drw-close:hover  { filter: brightness(1.35); }
  .drw-close:active { transform: scale(.92); }

  /*
   * Drawer ka apna header: halka band, naam baayein, ✕ daayein.
   *
   * Pehle yeh site header jaisa peela tha aur naam bade caps me. Reference me
   * band neutral hai — us se neeche ka peela DEPOSIT/REGISTRATION button hi
   * nazar me sabse pehle aata hai, jo asli maqsad hai. Peele band par peela
   * button apne hi rang me kho jaata tha.
   */
  .drw-head {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 11px 14px;
    background: var(--cb-surface, #f5f5f5);
    border-bottom: 1px solid var(--cb-line, #eeecec);
  }
  .drw-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: .01em;
    color: var(--cb-ink-strong, #111110);
  }

  .drw-cta {
    width: 100%;
    padding: 12px 14px;
    border: none;
    border-radius: var(--cb-radius-s, .5rem);
    background: var(--cb-yellow, #ffb700);
    color: var(--cb-ink, #343131);
    font-size: 13px; font-weight: 800;
    letter-spacing: .05em; text-transform: uppercase;
    cursor: pointer;
    transition: filter .15s, transform .1s;
  }
  .drw-cta:hover  { filter: brightness(1.06); }
  .drw-cta:active { transform: scale(.98); }
  .drw-cta--dark  { background: var(--cb-ink, #343131); color: #fff; }

  .drw-auth { display: flex; flex-direction: column; gap: 8px; }

  .drw-acct {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 10px; padding: 9px 12px;
    border-radius: var(--cb-radius-s, .5rem);
    background: var(--cb-surface, #f5f5f5);
  }
  .drw-acct-name { font-size: 12.5px; font-weight: 700; color: var(--cb-ink, #343131); }
  .drw-acct-bal  { font-size: 13px;  font-weight: 800; color: var(--cb-green, #16b14a); }

  .drw-apps {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; margin: 16px 0 14px;
  }
  .drw-apps-label {
    font-size: 10.5px; font-weight: 800;
    letter-spacing: .07em; text-transform: uppercase;
    color: var(--cb-text-40, rgba(52,49,49,.4));
  }
  .drw-apps-icons { display: flex; align-items: center; gap: 10px; color: var(--cb-ink, #343131); }
  .drw-apps-icons button,
  .drw-apps-icons a {
    display: flex; align-items: center;
    border: none; background: none; padding: 0;
    color: inherit; cursor: pointer;
  }
  .drw-apps-icons button:hover, .drw-apps-icons a:hover { opacity: .7; }
  .drw-apps-tip {
    margin: -6px 0 14px;
    padding: 8px 10px;
    font-size: 11.5px;
    line-height: 1.35;
    color: var(--cb-ink, #343131);
    background: var(--cb-surface, #f5f5f5);
    border-radius: var(--cb-radius-s, .5rem);
  }
  .drw-apps-tip strong { font-weight: 800; }

  /*
   * Welcome-offer panel.
   *
   * Green, and its own colour rather than the signupBonuses gradient: that
   * gradient also paints the registration sheet's three choosable cards, where
   * each offer needs a colour of its own to be told apart. One panel in the
   * drawer has no such job, so it follows the reference's green instead of
   * borrowing whichever colour the first card happens to use.
   */
  .drw-promo {
    padding: 14px 14px 16px;
    border-radius: var(--cb-radius-m, .75rem);
    background: linear-gradient(155deg, #12b46a 0%, #22c95f 52%, #63dd6b 100%);
    color: #fff;
    text-align: center;
    margin-bottom: 16px;
  }
  .drw-promo-art {
    display: block;
    width: 100%;
    max-width: 172px;
    height: auto;
    margin: 0 auto 10px;
  }
  .drw-promo-kicker { margin: 0 0 4px; font-size: 11.5px; opacity: .92; }
  .drw-promo-title  { margin: 0 0 12px; font-size: 16px; font-weight: 800; line-height: 1.25; }
  .drw-promo-btn {
    width: 100%;
    padding: 10px 12px;
    border: none;
    border-radius: var(--cb-radius-s, .5rem);
    background: var(--cb-yellow, #ffb700);
    color: var(--cb-ink, #343131);
    font-size: 12.5px; font-weight: 800;
    letter-spacing: .05em; text-transform: uppercase;
    cursor: pointer;
  }
  .drw-promo-btn:hover { filter: brightness(1.06); }


  /*
   * Plain rows, not plates. An earlier pass filled every row grey — that fill
   * belongs to the reference's SUB-links, while these top-level entries sit on
   * the drawer's own white with nothing behind them. The glyph does the work,
   * so it is bigger and darker than the label rather than a matching-weight
   * pair of them.
   */
  .drw-list { display: flex; flex-direction: column; }
  .drw-row {
    position: relative;
    display: flex; align-items: center; gap: 16px;
    box-sizing: border-box;
    width: 100%;
    min-height: 44px;
    padding: 6px 4px;
    border: none;
    background: none;
    color: var(--cb-ink-strong, #111110);
    font-size: 15px; font-weight: 600;
    text-align: left;
    cursor: pointer;
    transition: color .15s;
  }
  .drw-row:hover      { color: var(--cb-yellow-dark, #d39906); }
  .drw-row.is-active  { color: var(--cb-yellow-dark, #d39906); font-weight: 800; }
  .drw-row-ico {
    flex: 0 0 auto;
    width: 26px;
    font-size: 19px;
    display: flex; align-items: center; justify-content: center;
    color: inherit;
  }
  .drw-row-label { flex: 1 1 auto; min-width: 0; }
  .drw-row-badge {
    flex: 0 1 auto;
    padding: 3px 8px;
    border-radius: var(--cb-radius-xxs, .25rem);
    background: var(--cb-yellow, #ffb700);
    color: var(--cb-ink, #343131);
    font-size: 11.5px; font-weight: 800;
    letter-spacing: .04em; text-transform: uppercase;
  }
  .drw-foot {
    margin: 16px 0 0;
    text-align: center;
    font-size: 10px;
    color: var(--cb-text-40, rgba(52,49,49,.4));
  }
`;

export default Sidebar;
